import {
  OrgUserSearchResult,
  OrgUserSearchResultResourceProgram,
  RewardToken,
  UserId,
} from '@cibo/core'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath } from 'react-router-dom'
import { usePrograms } from '../../queries'
import { ProgramIcon } from '../ProgramIcon'

type CellProps = {
  programs: OrgUserSearchResultResourceProgram[]
  userId: UserId
  userProgramPath: string
}

const PotentialRewards = ({ programs, userId, userProgramPath }: CellProps) => {
  const { t } = useTranslation('@cibo/programs/UserColumns')
  const allPrograms = usePrograms()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const rewardablePrograms = useMemo(
    () => programs.filter(program => Object.keys(program?.rewards?.potential ?? {}).length > 0),
    []
  )
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    !!rewardablePrograms.length && setAnchorEl(event.currentTarget)
    event.stopPropagation()
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        disabled={!rewardablePrograms.length}
      >
        {t('rewards', { count: rewardablePrograms?.length ?? 0 })}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {rewardablePrograms?.map(({ programId, rewards }, index) => {
          const program = allPrograms.data?.find(program => program.programId === programId)
          return (
            <MenuItem
              key={programId}
              component={Link}
              to={generatePath(userProgramPath, { userId: userId.toString(), programId })}
            >
              <ListItemIcon>
                {!!program && <ProgramIcon program={program.program} size={24} />}
              </ListItemIcon>
              <ListItemText
                primary={program?.program.content?.fields?.title}
                secondary={
                  <>
                    {t('upto', {
                      val: Object.keys(rewards.potential).map(token =>
                        t(`token_${token}`, {
                          count: rewards.potential[token as RewardToken].amount,
                        })
                      ),
                    })}
                  </>
                }
              />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

const EligiblePrograms = ({ programs, userProgramPath, userId }: CellProps) => {
  const { t } = useTranslation('@cibo/programs/UserColumns')
  const allPrograms = usePrograms()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const eligiblePrograms = useMemo(() => programs?.filter(program => program.eligible), [])
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    !!eligiblePrograms && setAnchorEl(event.currentTarget)
    event.stopPropagation()
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        disabled={!eligiblePrograms.length}
      >
        {t('programs', { count: eligiblePrograms?.length ?? 0 })}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {eligiblePrograms?.map(({ programId }, index) => {
          const program = allPrograms.data?.find(program => programId === program.programId)
          return (
            <MenuItem
              key={programId}
              component={Link}
              to={generatePath(userProgramPath, { userId: userId.toString(), programId })}
            >
              <ListItemIcon>
                {!!program && <ProgramIcon program={program.program} size={24} />}
              </ListItemIcon>
              <ListItemText primary={program?.program.content?.fields?.title} />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

type UseOrgUserProgramColumnsProps = Omit<GridColDef<OrgUserSearchResult>, 'field'> &
  Partial<Pick<GridColDef<OrgUserSearchResult>, 'field'>> & {
    userProgramPath: string
  }

export const useOrgUserProgramColumns = ({
  userProgramPath,
  ...params
}: UseOrgUserProgramColumnsProps) => {
  const { t } = useTranslation('@cibo/programs/UserColumns')

  return {
    potentialRewards: {
      field: 'potentialRewards',
      headerName: t('potentialRewards'),
      minWidth: 150,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: ({
        row,
        value,
      }: GridRenderCellParams<OrgUserSearchResult['resources'], OrgUserSearchResult>) => (
        <PotentialRewards
          programs={row.resources?.programs ?? []}
          userId={row.userId}
          userProgramPath={userProgramPath}
        />
      ),
      ...params,
    },
    eligiblePrograms: {
      field: 'eligiblePrograms',
      headerName: t('eligiblePrograms'),
      minWidth: 150,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: ({
        row,
        value,
      }: GridRenderCellParams<OrgUserSearchResult['resources'], OrgUserSearchResult>) => (
        <EligiblePrograms
          programs={row.resources?.programs ?? []}
          userId={row.userId}
          userProgramPath={userProgramPath}
        />
      ),
      ...params,
    },
  } as Record<string, GridColDef<OrgUserSearchResult>>
}
